import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Typography, Breadcrumb, Button, message } from 'antd'
import { HomeOutlined, ProjectOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import { useProjects } from '../../hooks/useProjects'
import KanbanBoard from '../../components/Projects/KanbanBoard'

const { Title } = Typography

const ProjectTasksPage = () => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [componentError, setComponentError] = useState(null)

  // Debug logging
  console.log('🔍 ProjectTasksPage - projectId:', projectId)
  console.log('🔍 ProjectTasksPage - user:', user)

  // ใช้ useProjects hook และ getProjectById เพื่อดึงข้อมูลโปรเจ็คตาม ID
  const projectsHook = useProjects({ autoFetch: false })
  const { getProjectById } = projectsHook
  const [projectData, setProjectData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Error handler สำหรับ component
  const handleError = React.useCallback((err) => {
    console.error('❌ ProjectTasksPage - Component error:', err)
    setComponentError(err.message || 'เกิดข้อผิดพลาดในการโหลดหน้า')
  }, [])

  console.log('🔍 ProjectTasksPage - projectId:', projectId)
  console.log('🔍 ProjectTasksPage - projectsHook:', projectsHook)
  console.log('🔍 ProjectTasksPage - getProjectById function:', typeof getProjectById)
  console.log('🔍 ProjectTasksPage - getProjectById available:', !!getProjectById)

  // ตรวจสอบสิทธิ์การเข้าถึง
  const canAccessTasks = user?.permissions?.project_tasks?.read === true
  const canViewProject = user?.permissions?.projects?.read === true

  console.log('🔍 ProjectTasksPage - canAccessTasks:', canAccessTasks)
  console.log('🔍 ProjectTasksPage - canViewProject:', canViewProject)

  // จัดการเมื่อไม่มีสิทธิ์
  React.useEffect(() => {
    if (canAccessTasks === false) {
      message.error('คุณไม่มีสิทธิ์ในการเข้าถึงระบบจัดการงานย่อย')
      navigate('/projects')
    }
  }, [canAccessTasks, navigate])

  // จัดการเมื่อไม่พบโครงการ
  React.useEffect(() => {
    if (projectId && !loading && !projectData && error) {
      console.log('❌ ProjectTasksPage - Not found project, navigating to /projects')
      message.error('ไม่พบโครงการที่ระบุ')
      navigate('/projects')
    }
  }, [projectId, projectData, loading, error, navigate])

  // ดึงข้อมูลโปรเจ็คตาม ID เมื่อ projectId เปลี่ยน
  React.useEffect(() => {
    let isMounted = true // Track component mount state

    const fetchProjectData = async () => {
      console.log('🔍 ProjectTasksPage - Starting fetch, projectId:', projectId)
      console.log('🔍 ProjectTasksPage - getProjectById available:', !!getProjectById)

      if (!projectId) {
        console.log('❌ No projectId provided')
        if (isMounted) {
          setProjectData(null)
          setLoading(false)
        }
        return
      }

      if (!getProjectById) {
        console.log('❌ getProjectById not available yet')
        return
      }

      try {
        console.log('📡 Calling getProjectById with projectId:', projectId)

        if (isMounted) {
          setLoading(true)
          setError(null)
        }

        const project = await getProjectById(projectId)

        console.log('✅ Project fetched successfully:', project)

        if (isMounted) {
          setProjectData(project)
          setLoading(false)
        }
      } catch (err) {
        console.error('❌ Failed to fetch project:', err)

        if (isMounted) {
          setError(err.message || 'ไม่สามารถดึงข้อมูลโครงการได้')
          setLoading(false)
        }
      }
    }

    // เรียกใช้งาน async function เฉพาะเมื่อ dependencies พร้อม
    if (projectId && getProjectById) {
      fetchProjectData().catch(err => {
        console.error('❌ Unhandled error in fetchProjectData:', err)
      })
    }

    // Cleanup function ที่ชัดเจน
    return () => {
      isMounted = false
      console.log('🧹 ProjectTasksPage - useEffect cleanup executed')
    }
  }, [projectId, getProjectById])

  // จัดการ component error
  React.useEffect(() => {
    if (componentError) {
      console.error('❌ ProjectTasksPage - Component error:', componentError)
    }
  }, [componentError])

  const handleBackToProjects = () => {
    navigate('/projects')
  }

  if (componentError) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4} type="danger">เกิดข้อผิดพลาดในหน้านี้</Title>
        <p>รายละเอียด: {componentError}</p>
        <Button type="primary" onClick={handleBackToProjects}>
          กลับไปหน้าโครงการ
        </Button>
      </div>
    )
  }

  if (!canAccessTasks) {
    return null // จะ redirect ใน useEffect
  }

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>กำลังโหลดข้อมูล...</Title>
      </div>
    )
  }

  if (!projectData) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Title level={4} type="danger">ไม่พบข้อมูลโครงการ</Title>
        <p>รายละเอียด: {error || 'ไม่ระบุ'}</p>
        <Button type="primary" onClick={handleBackToProjects}>
          กลับไปหน้าโครงการ
        </Button>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Breadcrumb */}
      <div style={{
        backgroundColor: '#fff',
        padding: '12px 24px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
            <span>หน้าหลัก</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item href="/projects">
            <ProjectOutlined />
            <span>จัดการโครงการ</span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <span>{projectData?.project_name || 'โครงการ'}</span>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>


      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        backgroundColor: '#fff',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBackToProjects}
          >
            กลับ
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {projectData?.project_name || 'โครงการ'}
            </Title>
            {projectData?.project_code && (
              <div style={{ color: '#666', fontSize: 14 }}>
                รหัสโครงการ: {projectData.project_code}
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#666',
          fontSize: 14
        }}>
          <span>
            สถานะ: {getStatusText(projectData?.status)}
          </span>
          <span>
            ความคืบหน้า: {projectData?.progress_percentage || 0}%
          </span>
          {projectData?.manager_name && (
            <span>
              ผู้จัดการ: {projectData.manager_name}
            </span>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <div style={{ padding: '16px 0' }}>
        <React.Suspense fallback={
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Title level={4}>กำลังโหลด Kanban Board...</Title>
          </div>
        }>
          <React.StrictMode>
            <KanbanBoard
              projectId={projectId}
              projectInfo={projectData}
            />
          </React.StrictMode>
        </React.Suspense>
      </div>
    </div>
  )
}

// Error Boundary Component
class ProjectTasksPageErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ ProjectTasksPage - Error Boundary caught:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24 }}>
          <Title level={4} type="danger">เกิดข้อผิดพลาดใน ProjectTasksPage</Title>
          <p>รายละเอียด: {this.state.error?.message || 'Unknown Error'}</p>
          <Button type="primary" onClick={() => window.location.reload()}>
            โหลดหน้าใหม่
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

// Export with Error Boundary
export default function ProjectTasksPageWrapper() {
  return (
    <ProjectTasksPageErrorBoundary>
      <ProjectTasksPage />
    </ProjectTasksPageErrorBoundary>
  )
}

// Export for direct use if needed
export { ProjectTasksPage }

// Helper function สำหรับแปลงสถานะ
const getStatusText = (status) => {
  const statusMap = {
    planning: 'วางแผน',
    active: 'กำลังดำเนินการ',
    on_hold: 'พักชั่วคราว',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก'
  }
  return statusMap[status] || status
}
